// Modelos según documentación API v1 - Módulo Entry
import type { BatchSummary } from '@shared/models/BatchSummary';

// Estados de entrada
export type EntryStatus = 'PENDIENTE' | 'EN_RECEPCION' | 'EN_RECEPCION_PARCIAL' | 'FINALIZADA' | 'CANCELADA';

// Estados de albarán de recepción
export type ReceiptStatus = 'ABIERTA' | 'COMPLETADA' | 'CANCELADA';

// Tipo de movimiento
export type MovementType = 'ACCEPTED' | 'REJECTED';

// Estado de despacho
export type DispatchStatus = 'PENDING' | 'DISPATCHED';

// Tipo de despacho
export type DispatchType = 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER' | null;

// Resultado de requisitos
export type RequirementsResult = 'CORRECTA' | 'INCUMPLE';

// Item de tiempo de separación
export interface SeparationTimeItem {
  id: number;
  separation_time_id: number;
  product_id: number;
  qty: number;
  product?: {
    id: number;
    name: string;
    code: string;
    img?: string;
  };
}

// Tiempo de separación
export interface SeparationTime {
  id: number;
  receipt_id: number;
  time_minutes: number;
  user_id: number;
  created_at: string;
  items: SeparationTimeItem[];
  user?: {
    id: number;
    name: string;
    email?: string;
  };
}

// Producto en línea de entrada
export interface EntryLineProduct {
  id: number;
  name: string;
  code: string;
  image_hash?: string | null;
  img?: string | null;
}

// Línea de entrada
export interface EntryLine {
  id: number;
  entry_id: number;
  product_id: number;
  expected_qty: number;
  qty_accept?: number; // Cantidad aceptada
  qty_rejects?: number; // Cantidad rechazada
  metadata: Record<string, any> | null;
  product?: EntryLineProduct;
}

// Requisito de calidad
export interface EntryReceiptRequirement {
  id: number;
  receipt_id: number;
  requirement_code: string;
  is_noncompliance: boolean;
  notes: string | null;
  attachments: string[];
  user_id: number;
  created_at: string;
  updated_at: string;
}

// Movimiento de recepción
export interface EntryReceiptMovement {
  id: number;
  receipt_id: number;
  product_id: number;
  movement_type: MovementType;
  qty: number;
  location_id: number;
  batch_id?: number | null;
  batch?: BatchSummary | null;
  rejection_type: string | null;
  rejection_text: string | null;
  attachments: string[];
  dispatch_status: DispatchStatus;
  dispatch_type: DispatchType;
  dispatched_at: string | null;
  user_id: number;
  created_at: string;
  updated_at: string;
  // Relaciones (cuando se solicitan)
  product?: {
    id: number;
    name: string;
    code: string;
    img?: string;
  };
  location?: {
    id: number;
    code: string;
  };
  user?: {
    id: number;
    name: string;
    email?: string;
  } | null;
}

// Albarán de recepción
export interface EntryReceipt {
  id: number;
  entry_id: number;
  code: string;
  status: ReceiptStatus;
  started_at: string;
  deadline_at: string;
  requirements_result: RequirementsResult;
  cancel_reason: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  requirements?: EntryReceiptRequirement[];
  movements?: EntryReceiptMovement[];
  separation_times?: SeparationTime[];
}

// Información del equipo/cliente
export interface EntryTeam {
  id: number;
  name: string;
}

// Información del usuario
export interface EntryUser {
  id: number;
  name: string;
}

// Entrada principal
export interface Entry {
  id: number;
  user_id: number;
  team_id: number;
  reference: string;
  status: EntryStatus;
  estimated_delivery_at: string | null;
  boxes_count: number | null;
  tracking_code: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  entry_lines?: EntryLine[];
  entry_receipts?: EntryReceipt[];
  team?: EntryTeam;
  user?: EntryUser;
}

// Filtros para búsqueda de entradas
export interface EntryFilters {
  // Paginación
  per_page?: number;
  page?: number;

  // Ordenamiento
  sorting?: {
    column: 'id' | 'reference' | 'created_at' | 'estimated_delivery_at';
    direction: 'asc' | 'desc';
  };

  // Filtros avanzados
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator:
        | 'equals'
        | 'not_equals'
        | 'contains'
        | 'not_contains'
        | 'starts_with'
        | 'ends_with'
        | 'greater_than'
        | 'less_than'
        | 'greater_equal'
        | 'less_equal'
        | 'in'
        | 'not_in'
        | 'is_null'
        | 'is_not_null';
      value?: any;
    }>;
    logic: 'AND' | 'OR';
  };

  // Búsqueda simple (para compatibilidad)
  searchTerm?: string;

  /** Filtro por productos: param directo product_ids=1,2,3 (no advanced_filters) */
  product_ids?: number[];
}

// Response paginada de la API (meta puede venir en snake_case o camelCase)
export interface EntryApiResponse {
  data: Entry[];
  meta: {
    current_page?: number;
    currentPage?: number;
    per_page?: number;
    perPage?: number;
    total?: number;
    last_page?: number;
    lastPage?: number;
  };
}

// Para useInfiniteQuery
export interface EntryPage {
  entries: Entry[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

// Mapeo de estados para mostrar en la UI
export const ENTRY_STATUS_LABELS: Record<EntryStatus, string> = {
  PENDIENTE: 'Pendiente',
  EN_RECEPCION: 'En recepción',
  EN_RECEPCION_PARCIAL: 'En recepción parcial',
  FINALIZADA: 'Finalizada',
  CANCELADA: 'Cancelada',
};

// Función helper para obtener status label con i18n
export const getEntryStatusLabel = (status: EntryStatus, t?: (key: string) => string): string => {
  if (!t) return ENTRY_STATUS_LABELS[status] || 'Desconocido';

  switch (status) {
    case 'PENDIENTE':
      return t('status.pending');
    case 'EN_RECEPCION':
      return t('status.in_reception');
    case 'EN_RECEPCION_PARCIAL':
      return t('status.partial_reception');
    case 'FINALIZADA':
      return t('status.finished');
    case 'CANCELADA':
      return t('status.cancelled');
    default:
      return t('status.unknown');
  }
};

// Mapeo de colores para estados (usando clases CSS variables para light/dark mode)
export const ENTRY_STATUS_COLORS: Record<EntryStatus, string> = {
  PENDIENTE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  EN_RECEPCION: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  EN_RECEPCION_PARCIAL: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  FINALIZADA: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  CANCELADA: 'bg-bg-error text-error',
};

// Mapeo de colores para estados de albarán (usando clases CSS variables para light/dark mode)
export const RECEIPT_STATUS_COLORS: Record<ReceiptStatus, string> = {
  ABIERTA: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
  COMPLETADA: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  CANCELADA: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
};

// Mapeo de estados de albarán para mostrar en la UI
export const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  ABIERTA: 'Abierta',
  COMPLETADA: 'Completada',
  CANCELADA: 'Cancelada',
};

// Función helper para obtener status label de albarán con i18n
export const getReceiptStatusLabel = (status: ReceiptStatus, t?: (key: string) => string): string => {
  if (!t) return RECEIPT_STATUS_LABELS[status] || 'Desconocido';

  switch (status) {
    case 'ABIERTA':
      return t('receipt.status.open');
    case 'COMPLETADA':
      return t('receipt.status.completed');
    case 'CANCELADA':
      return t('receipt.status.cancelled');
    default:
      return t('receipt.status.unknown');
  }
};

const ENTRY_STATUS_VALUES: EntryStatus[] = [
  'PENDIENTE',
  'EN_RECEPCION',
  'EN_RECEPCION_PARCIAL',
  'FINALIZADA',
  'CANCELADA',
];

export const isEntryStatus = (status: string | null | undefined): status is EntryStatus => {
  if (!status) return false;
  return ENTRY_STATUS_VALUES.includes(status as EntryStatus);
};

/**
 * Obtiene el estado efectivo para UI.
 * Si existe alguna recepción cancelada, se prioriza CANCELADA aunque entry.status venga con valores no contemplados.
 */
export const getEntryEffectiveStatus = (entry: Pick<Entry, 'status' | 'entry_receipts'>): EntryStatus | null => {
  const cancelledReceipt = entry.entry_receipts?.find((receipt) => receipt.status === 'CANCELADA');
  if (cancelledReceipt) return 'CANCELADA';

  if (isEntryStatus(entry.status)) return entry.status;
  return null;
};

/**
 * Obtiene la recepción cancelada más reciente (si existe) para mostrar información adicional como motivo.
 */
export const getLatestCancelledReceipt = (entry: Pick<Entry, 'entry_receipts'>): EntryReceipt | null => {
  const cancelledReceipts = (entry.entry_receipts || []).filter((receipt) => receipt.status === 'CANCELADA');
  if (cancelledReceipts.length === 0) return null;

  return cancelledReceipts.sort((a, b) => {
    const dateA = new Date(a.cancelled_at || a.updated_at || a.created_at).getTime();
    const dateB = new Date(b.cancelled_at || b.updated_at || b.created_at).getTime();
    return dateB - dateA;
  })[0];
};
